// Communication Preferences Service for TyreLink
// Handles user notification preferences and settings

import { supabase } from './supabase'
import type {
  CommunicationPreferences,
  CreateCommunicationPreferences,
  UpdateCommunicationPreferences,
  NotificationPreferencesSummary,
  MessagingServiceResponse,
  NotificationType
} from '../types/messaging'

class CommunicationPreferencesService {
  async getPreferences(userId: string): Promise<MessagingServiceResponse<CommunicationPreferences>> {
    try {
      const { data, error } = await supabase
        .from('communication_preferences')
        .select('*')
        .eq('user_id', userId)
        .single()

      if (error) {
        // If preferences don't exist, create default ones
        if (error.code === 'PGRST116') {
          return this.createDefaultPreferences(userId)
        }
        
        return {
          success: false,
          error: {
            code: 'MESSAGE_FAILED',
            message: error.message
          }
        }
      }

      return {
        success: true,
        data
      }
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'MESSAGE_FAILED',
          message: 'Failed to fetch communication preferences',
          details: error
        }
      }
    }
  }

  async createDefaultPreferences(userId: string): Promise<MessagingServiceResponse<CommunicationPreferences>> {
    try {
      const defaultPreferences: CreateCommunicationPreferences = {
        user_id: userId,
        email_enabled: true,
        email_bookings: true,
        email_quotes: true,
        email_messages: true,
        email_marketing: false,
        whatsapp_enabled: false,
        whatsapp_bookings: false,
        whatsapp_quotes: false,
        whatsapp_messages: false,
        system_enabled: true,
        system_bookings: true,
        system_quotes: true,
        system_messages: true,
        quiet_hours_start: '22:00',
        quiet_hours_end: '08:00',
        timezone: 'Africa/Johannesburg'
      }

      const { data, error } = await supabase
        .from('communication_preferences')
        .insert([defaultPreferences])
        .select()
        .single()

      if (error) {
        return {
          success: false,
          error: {
            code: 'MESSAGE_FAILED',
            message: error.message
          }
        }
      }

      return {
        success: true,
        data
      }
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'MESSAGE_FAILED',
          message: 'Failed to create default preferences',
          details: error
        }
      }
    }
  }

  async updatePreferences(userId: string, updates: UpdateCommunicationPreferences): Promise<MessagingServiceResponse<CommunicationPreferences>> {
    try {
      const { data, error } = await supabase
        .from('communication_preferences')
        .update(updates)
        .eq('user_id', userId)
        .select()
        .single()

      if (error) {
        return {
          success: false,
          error: {
            code: 'MESSAGE_FAILED',
            message: error.message
          }
        }
      }

      return {
        success: true,
        data
      }
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'MESSAGE_FAILED',
          message: 'Failed to update preferences',
          details: error
        }
      }
    }
  }

  async getPreferencesSummary(userId: string): Promise<MessagingServiceResponse<NotificationPreferencesSummary>> {
    try {
      const preferencesResult = await this.getPreferences(userId)
      
      if (!preferencesResult.success || !preferencesResult.data) {
        return {
          success: false,
          error: preferencesResult.error || {
            code: 'MESSAGE_FAILED',
            message: 'Failed to get preferences'
          }
        }
      }

      const prefs = preferencesResult.data
      const now = new Date()
      const currentTime = now.toTimeString().slice(0, 5) // HH:MM format

      // Check if current time is within quiet hours
      const isQuietHoursActive = this.isInQuietHours(currentTime, prefs.quiet_hours_start, prefs.quiet_hours_end)

      // Count enabled notification types
      let emailCount = 0
      let whatsappCount = 0
      let systemCount = 0

      if (prefs.email_enabled) {
        if (prefs.email_bookings) emailCount++
        if (prefs.email_quotes) emailCount++
        if (prefs.email_messages) emailCount++
        if (prefs.email_marketing) emailCount++
      }

      if (prefs.whatsapp_enabled) {
        if (prefs.whatsapp_bookings) whatsappCount++
        if (prefs.whatsapp_quotes) whatsappCount++
        if (prefs.whatsapp_messages) whatsappCount++
      }

      if (prefs.system_enabled) {
        if (prefs.system_bookings) systemCount++
        if (prefs.system_quotes) systemCount++
        if (prefs.system_messages) systemCount++
      }

      // Determine preferred channels
      const preferredChannels: NotificationType[] = []
      if (emailCount > 0) preferredChannels.push('email')
      if (whatsappCount > 0) preferredChannels.push('whatsapp')
      if (systemCount > 0) preferredChannels.push('system')

      const summary: NotificationPreferencesSummary = {
        email_count: emailCount,
        whatsapp_count: whatsappCount,
        system_count: systemCount,
        quiet_hours_active: isQuietHoursActive,
        preferred_channels: preferredChannels
      }

      return {
        success: true,
        data: summary
      }
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'MESSAGE_FAILED',
          message: 'Failed to generate preferences summary',
          details: error
        }
      }
    }
  }

  private isInQuietHours(currentTime: string, startTime: string, endTime: string): boolean {
    // Convert time strings to minutes for easier comparison
    const timeToMinutes = (time: string): number => {
      const [hours, minutes] = time.split(':').map(Number)
      return hours * 60 + minutes
    }

    const current = timeToMinutes(currentTime)
    const start = timeToMinutes(startTime)
    const end = timeToMinutes(endTime)

    // Handle overnight quiet hours (e.g., 22:00 to 08:00)
    if (start > end) {
      return current >= start || current <= end
    }
    
    // Handle same-day quiet hours (e.g., 12:00 to 14:00)
    return current >= start && current <= end
  }

  async shouldSendNotification(
    userId: string, 
    notificationType: NotificationType, 
    category: 'bookings' | 'quotes' | 'messages' | 'marketing'
  ): Promise<MessagingServiceResponse<boolean>> {
    try {
      const preferencesResult = await this.getPreferences(userId)
      
      if (!preferencesResult.success || !preferencesResult.data) {
        // Default to allowing notifications if preferences can't be fetched
        return {
          success: true,
          data: true
        }
      }

      const prefs = preferencesResult.data
      const now = new Date()
      const currentTime = now.toTimeString().slice(0, 5)

      // Check quiet hours for non-urgent notifications
      if (category !== 'bookings' && this.isInQuietHours(currentTime, prefs.quiet_hours_start, prefs.quiet_hours_end)) {
        return {
          success: true,
          data: false
        }
      }

      // Check specific notification type and category preferences
      let shouldSend = false

      switch (notificationType) {
        case 'email':
          shouldSend = prefs.email_enabled && this.getEmailPreference(prefs, category)
          break
        case 'whatsapp':
          shouldSend = prefs.whatsapp_enabled && !!prefs.whatsapp_number && this.getWhatsAppPreference(prefs, category)
          break
        case 'system':
          shouldSend = prefs.system_enabled && this.getSystemPreference(prefs, category)
          break
      }

      return {
        success: true,
        data: shouldSend
      }
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'MESSAGE_FAILED',
          message: 'Failed to check notification preferences',
          details: error
        }
      }
    }
  }

  private getEmailPreference(prefs: CommunicationPreferences, category: string): boolean {
    switch (category) {
      case 'bookings': return prefs.email_bookings
      case 'quotes': return prefs.email_quotes
      case 'messages': return prefs.email_messages
      case 'marketing': return prefs.email_marketing
      default: return false
    }
  }

  private getWhatsAppPreference(prefs: CommunicationPreferences, category: string): boolean {
    switch (category) {
      case 'bookings': return prefs.whatsapp_bookings
      case 'quotes': return prefs.whatsapp_quotes
      case 'messages': return prefs.whatsapp_messages
      case 'marketing': return false // WhatsApp marketing not supported
      default: return false
    }
  }

  private getSystemPreference(prefs: CommunicationPreferences, category: string): boolean {
    switch (category) {
      case 'bookings': return prefs.system_bookings
      case 'quotes': return prefs.system_quotes
      case 'messages': return prefs.system_messages
      case 'marketing': return false // System marketing notifications not supported
      default: return false
    }
  }

  async enableWhatsApp(userId: string, phoneNumber: string): Promise<MessagingServiceResponse<CommunicationPreferences>> {
    try {
      const updates: UpdateCommunicationPreferences = {
        whatsapp_enabled: true,
        whatsapp_number: phoneNumber,
        whatsapp_bookings: true,
        whatsapp_quotes: true,
        whatsapp_messages: false // Keep messages on email/system by default
      }

      return this.updatePreferences(userId, updates)
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'MESSAGE_FAILED',
          message: 'Failed to enable WhatsApp notifications',
          details: error
        }
      }
    }
  }

  async disableWhatsApp(userId: string): Promise<MessagingServiceResponse<CommunicationPreferences>> {
    try {
      const updates: UpdateCommunicationPreferences = {
        whatsapp_enabled: false,
        whatsapp_bookings: false,
        whatsapp_quotes: false,
        whatsapp_messages: false
      }

      return this.updatePreferences(userId, updates)
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'MESSAGE_FAILED',
          message: 'Failed to disable WhatsApp notifications',
          details: error
        }
      }
    }
  }
}

export const communicationPreferencesService = new CommunicationPreferencesService()
export default communicationPreferencesService